"use client";

import { useEffect, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { ShopDomainField } from "@/components/agents/shared/shop-domain-field";
import { ShopifyLiveDataNote } from "@/components/agents/shared/shopify-live-data-note";
import {
  ShopifyToolTile,
  getORDER_LOOKUP_LOCKED_REASON,
  getSHOPIFY_TOOL_TILE_DEFS,
  type ShopifyToolName,
} from "@/components/agents/shared/shopify-tool-tile";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2,
  HelpCircle,
  Bot,
  UserX,
  Forward,
  ChevronDown,
  ChevronUp,
  FileText,
  Sparkles,
  Settings,
  SkipForward,
  AlertTriangle,
  ShoppingBag,
  Calendar,
  CalendarClock,
  Wrench,
  Mail,
  MailCheck,
  Tag,
  UserCheck,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import { useEmailAgentConfig, type EmailAgent, type EmailAgentConfig } from "@/hooks/agents/email";
import { DEFAULT_GMAIL_LABELS } from "@/lib/agents/email/constants";
import {
  buildSendAllEmailCategorySettings,
  hasNonSendingEmailCategories,
} from "@/lib/agents/email/auto-reply-categories";
import { useEmailClassifierConfigQuery } from "@/hooks/queries/email-classifier-config";
import { ResetToRoleDefaultButton } from "@/components/agents/shared/reset-to-role-default-button";
import { PromptHistoryButton } from "@/components/agents/shared/prompt-history-button";
import { AGENT_PRIMARY_ROLE_USE_CASE } from "@/lib/ai/roles/types";
import { useCrmBinding, type CrmStage } from "@/hooks/integrations/use-crm-binding";
import { useShopifyIntegration } from "@/hooks/integrations/use-shopify-integration";
import { useShopifyStorefrontToolsAccess } from "@/hooks/use-shopify-storefront-tools-access";
import { GeneratePromptModal } from "@/components/agents/email/dialogs/generate-prompt-modal";
import { AgentModelOverride } from "@/components/ai/agent-model-override";
import { ToolInstructionsDialog } from "@/components/agents/shared/dialogs/tool-instructions-dialog";
import { HandoverPipelineSelector } from "@/components/agents/email/handover-pipeline-selector";
import { HandoverChannelPicker } from "@/components/notification-channels/handover-channel-picker";
import { PageLoader } from "@/components/ui/page-loader";
import { ScheduleEmailToolSettings } from "@/components/agent-tasks/schedule-email-tool-settings";
import { useCalendarIntegration, useGmailModifyIntegration } from "@/hooks/integrations/google";
import {
  WorkingHoursConfigEditor,
  MeetingDefaultsConfig,
} from "@/components/agents/shared/calendar";
import { OAuthScopeConnectionStatus } from "@/components/integrations/oauth";
import { AgentToolCategory } from "@/components/agents/shared/agent-tool-presentation";
import { CrmToolsGroup } from "@/components/agents/shared/crm-tools-group";
import { AgentDeliveryToolsGroup } from "@/components/agents/shared/agent-delivery-tools-group";
import { ScheduleCallbackToolSettings } from "@/components/agents/shared/schedule-callback-tool-settings";
import { useTranslations } from "next-intl"



interface AutoReplySettingsProps {
  agent: EmailAgent;
  section: "general" | "tools";
  classificationSection?: ReactNode;
  onDeliveryConfigChange?: (config: EmailAgentConfig) => void;
}

export function AutoReplySettings({ agent, section, classificationSection, onDeliveryConfigChange }: AutoReplySettingsProps) {
  const SHOPIFY_TOOL_TILE_DEFS = getSHOPIFY_TOOL_TILE_DEFS(useTranslations("Metadata.AgentsSharedShopifyToolTile"))
  const ORDER_LOOKUP_LOCKED_REASON = getORDER_LOOKUP_LOCKED_REASON(useTranslations("Metadata.AgentsSharedShopifyToolTile"))


  const tUi = useTranslations("Email.AgentsEmailTabsAutoReplySettings")
  const tCommonUi = useTranslations("Common")

  const { fetchWithAuth } = useAuth();
  const {
    config,
    isLoading,
    isSaving,
    error,
    loadConfig,
    updateAutoReply,
    updateHumanHandover,
    updateEmailForward,
    saveConfig,
  } = useEmailAgentConfig(agent.id);

  // Classifier categories live on the scoped AI-config chain, not in agent.config
  const { data: classifierConfig } = useEmailClassifierConfigQuery(agent.id);
  const availableCategories = classifierConfig?.classifier_labels ?? [];

  // CRM binding for pipeline/stage selection
  const {
    binding: crmBinding,
    pipelines: crmPipelines,
    selectedIntegration: crmSelectedIntegration,
    isLoadingPipelines: isLoadingCrmPipelines,
    isSaving: isSavingCrm,
    updateBindingConfig,
    getStagesForPipeline,
    hasTenantDefault: hasCrmTenantDefault,
  } = useCrmBinding({
    scopeType: "email_agent",
    scopeId: agent.id,
    autoLoad: true,
  });

  // Shopify integration check
  const { integrations: shopifyIntegrations } = useShopifyIntegration();
  const hasActiveShopify = shopifyIntegrations.some((i) => i.is_active && i.uses_api);
  // Storefront MCP tools (Catalog + Policies) are gated on having ≥1 active
  // shopify_integrations row — channel-agnostic after Phase 0.5 decoupling
  // (see docs/SHOPIFY_STOREFRONT_MCP.md). Tenants without any active
  // integration see the section hidden regardless of billing channel.
  const { allowed: hasStorefrontToolsAccess } = useShopifyStorefrontToolsAccess();

  // Calendar integration
  const {
    config: calendarConfig,
    inboxes: calendarInboxes,
    isLoading: isLoadingCalendar,
    isSaving: isSavingCalendar,
    error: calendarError,
    updateConfig: updateCalendarConfig,
    connectCalendar,
  } = useCalendarIntegration({ agentId: agent.id });

  // Gmail Modify integration (for mark as read and add labels)
  const {
    markAsReadEnabled,
    labels,
    inboxes: gmailModifyInboxes,
    isLoading: isLoadingGmailModify,
    isSaving: isSavingGmailModify,
    error: gmailModifyError,
    updateMarkAsReadEnabled,
    updateLabels,
    connectGmailModify,
  } = useGmailModifyIntegration({ agentId: agent.id });

  // System prompt state (loaded from scoped tenant_ai_configs, not agent)
  const [systemPrompt, setSystemPrompt] = useState("");
  const [systemPromptLoaded, setSystemPromptLoaded] = useState(false);
  const [isSavingPrompt, setIsSavingPrompt] = useState(false);
  const [promptError, setPromptError] = useState<string | null>(null);
  const [savedSystemPrompt, setSavedSystemPrompt] = useState("");

  // UI state
  const [handoverExpanded, setHandoverExpanded] = useState(false);
  const [emailForwardExpanded, setEmailForwardExpanded] = useState(false);
  const [shopifyToolsExpanded, setShopifyToolsExpanded] = useState(false);
  const [generatePromptModalOpen, setGeneratePromptModalOpen] = useState(false);
  const [handoverInstructionsOpen, setHandoverInstructionsOpen] = useState(false);
  const [emailForwardInstructionsOpen, setEmailForwardInstructionsOpen] = useState(false);
  const [scheduleCallbackInstructionsOpen, setScheduleCallbackInstructionsOpen] = useState(false);
  const [skipReplyInstructionsOpen, setSkipReplyInstructionsOpen] = useState(false);
  const [shopifyOrderInstructionsOpen, setShopifyOrderInstructionsOpen] = useState(false);
  const [shopifyCatalogInstructionsOpen, setShopifyCatalogInstructionsOpen] = useState(false);
  const [shopifyPoliciesInstructionsOpen, setShopifyPoliciesInstructionsOpen] = useState(false);
  const [skipReplyExpanded, setSkipReplyExpanded] = useState(false);
  const [calendarBookingExpanded, setCalendarBookingExpanded] = useState(false);
  const [calendarAvailabilityInstructionsOpen, setCalendarAvailabilityInstructionsOpen] = useState(false);
  const [calendarBookMeetingInstructionsOpen, setCalendarBookMeetingInstructionsOpen] = useState(false);
  const [crmUpdateFieldsInstructionsOpen, setCrmUpdateFieldsInstructionsOpen] = useState(false);
  const [crmMoveStageInstructionsOpen, setCrmMoveStageInstructionsOpen] = useState(false);
  const [markAsReadExpanded, setMarkAsReadExpanded] = useState(false);
  const [labelsExpanded, setLabelsExpanded] = useState(false);

  // Label input state - incoming (AI Read)
  const [incomingLabelName, setIncomingLabelName] = useState("");
  const [incomingLabelBgColor, setIncomingLabelBgColor] = useState<string>(DEFAULT_GMAIL_LABELS.incoming.backgroundColor);
  const [incomingLabelTextColor, setIncomingLabelTextColor] = useState<string>(DEFAULT_GMAIL_LABELS.incoming.textColor);

  // Label input state - outgoing (AI Response)
  const [outgoingLabelName, setOutgoingLabelName] = useState("");
  const [outgoingLabelBgColor, setOutgoingLabelBgColor] = useState<string>(DEFAULT_GMAIL_LABELS.outgoing.backgroundColor);
  const [outgoingLabelTextColor, setOutgoingLabelTextColor] = useState<string>(DEFAULT_GMAIL_LABELS.outgoing.textColor);

  // Sync label input state with config
  useEffect(() => {
    if (labels.incoming) {
      setIncomingLabelName(labels.incoming.name);
      setIncomingLabelBgColor(labels.incoming.backgroundColor);
      setIncomingLabelTextColor(labels.incoming.textColor);
    } else {
      setIncomingLabelName("");
      setIncomingLabelBgColor(DEFAULT_GMAIL_LABELS.incoming.backgroundColor);
      setIncomingLabelTextColor(DEFAULT_GMAIL_LABELS.incoming.textColor);
    }
    if (labels.outgoing) {
      setOutgoingLabelName(labels.outgoing.name);
      setOutgoingLabelBgColor(labels.outgoing.backgroundColor);
      setOutgoingLabelTextColor(labels.outgoing.textColor);
    } else {
      setOutgoingLabelName("");
      setOutgoingLabelBgColor(DEFAULT_GMAIL_LABELS.outgoing.backgroundColor);
      setOutgoingLabelTextColor(DEFAULT_GMAIL_LABELS.outgoing.textColor);
    }
  }, [labels]);

  // Enable confirmation dialog state
  const [enableDialogOpen, setEnableDialogOpen] = useState(false);
  const [pendingStartDate, setPendingStartDate] = useState("");

  // Handover pipeline stages
  const [handoverStages, setHandoverStages] = useState<CrmStage[]>([]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // Load handover stages when handover pipeline changes
  useEffect(() => {
    if (crmBinding?.config?.handover?.pipeline_id) {
      const stages = getStagesForPipeline(crmBinding.config.handover.pipeline_id);
      setHandoverStages(stages);
    } else {
      setHandoverStages([]);
    }
  }, [crmBinding?.config?.handover?.pipeline_id, crmPipelines, getStagesForPipeline]);

  // Load system prompt from scoped config API
  useEffect(() => {
    const loadSystemPrompt = async () => {
      try {
        const res = await fetchWithAuth(`/api/agents/email/auto-reply/system-prompt?agent_id=${agent.id}`);
        if (res.ok) {
          const data = await res.json();
          setSystemPrompt(data.system_prompt || "");
          setSavedSystemPrompt(data.system_prompt || "");
        }
      } catch (err) {
        console.error("Failed to load system prompt:", err);
      } finally {
        setSystemPromptLoaded(true);
      }
    };
    loadSystemPrompt();
  }, [agent.id, fetchWithAuth]);

  // Save system prompt handler (to scoped config)
  const handleSaveSystemPrompt = async () => {
    setPromptError(null);
    setIsSavingPrompt(true);
    try {
      const res = await fetchWithAuth("/api/agents/email/auto-reply/system-prompt", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          agent_id: agent.id,
          system_prompt: systemPrompt.trim(),
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to save system prompt");
      }
      setSavedSystemPrompt(systemPrompt.trim());
      toast.success(tUi("systemPromptSaved"));
    } catch (err) {
      setPromptError(err instanceof Error ? err.message : "Failed to save system prompt");
    } finally {
      setIsSavingPrompt(false);
    }
  };

  // Helpers for date validation
  const getTodayDateString = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const isStartDateValid = (startDate: string | undefined | null): boolean => {
    if (!startDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const oneDayAgo = new Date(today);
    oneDayAgo.setDate(oneDayAgo.getDate() - 1);
    const startDateObj = new Date(startDate + "T00:00:00");
    return startDateObj >= oneDayAgo;
  };

  // Handlers
  const handleToggleEnabled = async (enabled: boolean) => {
    if (enabled) {
      // Check if start date needs to be set or confirmed
      if (!isStartDateValid(autoReply.start_date)) {
        // Set default pending start date to today
        setPendingStartDate(getTodayDateString());
        setEnableDialogOpen(true);
        return;
      }
    }
    await updateAutoReply({ enabled });
  };

  const handleConfirmEnable = async () => {
    // Save the start date and enable
    await updateAutoReply({
      enabled: true,
      start_date: pendingStartDate,
    });
    setEnableDialogOpen(false);
    setPendingStartDate("");
  };

  const handleConfidenceChange = async (value: string) => {
    const confidence = Math.min(100, Math.max(0, parseInt(value) || 0));
    await updateAutoReply({ min_confidence: confidence });
  };

  const handleStartDateChange = async (date: string) => {
    await updateAutoReply({ start_date: date });
  };

  const handleUseAiToolsChange = async (useTools: boolean) => {
    await updateAutoReply({ use_ai_tools: useTools });
  };

  // Per-category settings handlers
  const getCategorySettings = (category: string) => {
    const settings = config.auto_reply?.category_settings?.[category];
    return {
      enabled: settings?.enabled ?? false,
      dry_run: settings?.dry_run ?? true, // Default to dry run for safety
      create_draft_instead_of_send: settings?.create_draft_instead_of_send ?? false,
    };
  };

  const handleCategoryToggle = async (category: string, enabled: boolean) => {
    const currentSettings = config.auto_reply?.category_settings || {};
    const categorySettings = currentSettings[category] || {};

    const newCategorySettings = {
      ...currentSettings,
      [category]: {
        ...categorySettings,
        enabled,
        // Set default dry_run: true when enabling for the first time
        dry_run: categorySettings.dry_run ?? true,
      },
    };

    // Also update selected_categories for backwards compatibility
    const currentCategories = config.auto_reply?.selected_categories || [];
    const newCategories = enabled
      ? currentCategories.includes(category) ? currentCategories : [...currentCategories, category]
      : currentCategories.filter((c) => c !== category);

    const savedConfig = await updateAutoReply({
      category_settings: newCategorySettings,
      selected_categories: newCategories,
    });
    onDeliveryConfigChange?.(savedConfig);
  };

  const handleEnableAllCategories = async () => {
    const categoryConfig = buildSendAllEmailCategorySettings(
      availableCategories,
      config.auto_reply?.category_settings ?? {},
      config.auto_reply?.selected_categories ?? [],
    );
    const savedConfig = await updateAutoReply(categoryConfig);
    onDeliveryConfigChange?.(savedConfig);
  };


  const handleCategoryDeliveryModeChange = async (category: string, mode: "dry_run" | "draft" | "send") => {
    const currentSettings = config.auto_reply?.category_settings || {};
    const categorySettings = currentSettings[category] || { enabled: true };

    const savedConfig = await updateAutoReply({
      category_settings: {
        ...currentSettings,
        [category]: {
          ...categorySettings,
          dry_run: mode === "dry_run",
          create_draft_instead_of_send: mode === "draft",
        },
      },
    });
    onDeliveryConfigChange?.(savedConfig);
  };

  const handleHandoverToggle = async (enabled: boolean) => {
    await updateHumanHandover({ enabled });
  };

  const handleEmailForwardToggle = async (enabled: boolean) => {
    await updateEmailForward({ enabled });
  };

  // Human escalation CRM handlers
  const handleHandoverPipelineChange = async (pipelineId: string) => {
    await updateBindingConfig({
      handover: {
        ...crmBinding?.config?.handover,
        pipeline_id: pipelineId,
        stage_id: undefined,
      },
    });
  };

  const handleHandoverStageChange = async (stageId: string) => {
    await updateBindingConfig({
      handover: {
        ...crmBinding?.config?.handover,
        stage_id: stageId,
      },
    });
  };

  const handleHandoverChannelsChange = async (ids: string[]) => {
    await updateHumanHandover({ notification_channel_ids: ids });
  };

  const handleApplyGeneratedPrompt = (prompt: string) => {
    setSystemPrompt(prompt);
  };

  // CRM availability check
  const isCrmEnabled = crmBinding && crmBinding.config?.enabled !== false;
  const hasCrmForHandover = isCrmEnabled && crmSelectedIntegration && crmPipelines.length > 0;

  const getQualificationColor = (label: string) => {
    const colors: Record<string, string> = {
      "Qualified Lead": "bg-green-100 text-green-800 border-green-200",
      "Support Request": "bg-blue-100 text-blue-800 border-blue-200",
      Spam: "bg-red-100 text-red-800 border-red-200",
      Other: "bg-gray-100 text-gray-800 border-gray-200",
    };
    return colors[label] || "bg-purple-100 text-purple-800 border-purple-200";
  };

  if (isLoading) {
    return <PageLoader message={section === "tools" ? tUi("loadingYourAgentSTools") : tUi("loadingAgentSettings")} />;
  }

  const autoReply = config.auto_reply ?? ({} as NonNullable<EmailAgentConfig['auto_reply']>);
  const shouldShowEnableAllCategories = hasNonSendingEmailCategories(
    availableCategories,
    autoReply.category_settings ?? {},
  );
  const humanHandover = config.human_handover ?? ({} as NonNullable<EmailAgentConfig['human_handover']>);
  const emailForward = config.email_forward ?? ({} as NonNullable<EmailAgentConfig['email_forward']>);
  const scheduleCallback = config.schedule_call ?? {};

  return (
    <div className="space-y-6">
      {(error || promptError) && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error || promptError}
        </div>
      )}

      {section === "general" && (
        <>
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <CardTitle><span key={tUi("systemPrompt")}>{tUi("systemPrompt")}</span></CardTitle>
                  <CardDescription className="mt-1.5">
                    <span key={tUi("defineTheBehaviorAndPersonalityOfYourEmail")}>{tUi("defineTheBehaviorAndPersonalityOfYourEmail")}</span></CardDescription>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <PromptHistoryButton scopeType="email_agent" scopeId={agent.id}
                    useCase={AGENT_PRIMARY_ROLE_USE_CASE.email_agent}
                    currentPrompt={systemPrompt} onRestore={setSystemPrompt} canRestore />
                  <ResetToRoleDefaultButton scopeType="email_agent"
                    useCase={AGENT_PRIMARY_ROLE_USE_CASE.email_agent}
                    roleSlugs={agent.config?.roles} roleLabel={agent.config?.role_label}
                    disabled={isSavingPrompt}
                    onReset={(composed) => setSystemPrompt(composed.system_prompt)} />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3">
                <div>
                  <Label htmlFor="auto-reply-toggle"><span key={tUi("enableAutoReply")}>{tUi("enableAutoReply")}</span></Label>
                  <p className="text-sm text-muted-foreground"><span key={tUi("whenEnabledTheAgentAutomaticallyRespondsToEligible")}>{tUi("whenEnabledTheAgentAutomaticallyRespondsToEligible")}</span></p>
                </div>
                <Switch id="auto-reply-toggle" checked={autoReply.enabled || false}
                  onCheckedChange={handleToggleEnabled} disabled={isSaving} />
              </div>
              <Textarea value={systemPrompt} onChange={(event) => setSystemPrompt(event.target.value)}
                placeholder={tUi("youAreAHelpfulCustomerSupportAgent")} rows={8} disabled={isSavingPrompt} />
              <div className="flex flex-col gap-2 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
                <Button type="button" variant="outline"
                  onClick={() => setGeneratePromptModalOpen(true)} className="w-full sm:w-auto">
                  <Sparkles className="mr-2 h-4 w-4" />
                  <span><span key={tUi("generateFromEmails")}>{tUi("generateFromEmails")}</span></span>
                </Button>
                <Button onClick={handleSaveSystemPrompt}
                  disabled={isSavingPrompt || !systemPromptLoaded || systemPrompt === savedSystemPrompt}
                  className="w-full sm:w-auto">
                  {isSavingPrompt ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /><span><span key={tCommonUi("saving")}>{tCommonUi("saving")}</span></span></> : tUi("savePrompt")}
                </Button>
              </div>
            </CardContent>
          </Card>
          {classificationSection}
          {/* Basic Config */}
          <Card id="email-category-settings" className="scroll-mt-6">
            <CardHeader>
              <CardTitle><span key={tUi("categorySettings")}>{tUi("categorySettings")}</span></CardTitle>
              <CardDescription>
                <span key={tUi("chooseCategoryReplyModesAndSafeguardsForIncoming")}>{tUi("chooseCategoryReplyModesAndSafeguardsForIncoming")}</span></CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Category Selection with Per-Category Settings */}
              <div className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2 sm:justify-start sm:gap-4">
                  <Label className="text-sm font-medium">
                    <span><span key={tUi("replyToCategories")}>{tUi("replyToCategories")}</span></span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-4 h-4 inline ml-1 text-gray-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <div className="space-y-2">
                          <p><span key={tUi("enableCategoriesAndConfigureDeliveryForEach")}>{tUi("enableCategoriesAndConfigureDeliveryForEach")}</span></p>
                          <ul className="text-xs space-y-1">
                            <li><span className="text-yellow-500 font-medium"><span key={tUi("dryRun")}>{tUi("dryRun")}</span></span> <span><span key={tUi("logOnlyNoEmailsSent")}>{tUi("logOnlyNoEmailsSent")}</span></span></li>
                            <li><span className="text-purple-500 font-medium"><span key={tUi("createDrafts")}>{tUi("createDrafts")}</span></span> <span><span key={tUi("saveAsGmailDraftsForReview")}>{tUi("saveAsGmailDraftsForReview")}</span></span></li>
                            <li><span className="text-green-500 font-medium"><span key={tUi("sending")}>{tUi("sending")}</span></span> <span><span key={tUi("sendEmailsAutomatically")}>{tUi("sendEmailsAutomatically")}</span></span></li>
                          </ul>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </Label>
                  {shouldShowEnableAllCategories && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8"
                      onClick={handleEnableAllCategories}
                      disabled={isSaving}
                    >
                      <span key={tUi("enableAll")}>{tUi("enableAll")}</span></Button>
                  )}
                </div>

                {availableCategories.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {availableCategories.map((category) => {
                      const categorySettings = getCategorySettings(category);
                      const isEnabled = categorySettings.enabled;
                      const isDryRun = categorySettings.dry_run;
                      const isCreateDraft = categorySettings.create_draft_instead_of_send;

                      return (
                        <div
                          key={category}
                          className={cn(
                            "border rounded-lg transition-all",
                            isEnabled
                              ? "bg-white border-gray-200"
                              : "bg-gray-50 border-gray-100 opacity-60"
                          )}
                        >
                          {/* Category Header */}
                          <div className="flex items-center justify-between p-2.5">
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={isEnabled}
                                onCheckedChange={(checked) => handleCategoryToggle(category, checked)}
                                disabled={isSaving}
                                className="scale-90"
                              />
                              <Badge
                                className={cn(
                                  "text-xs font-medium",
                                  getQualificationColor(category)
                                )}
                              >
                                {category}
                              </Badge>
                            </div>
                            {isEnabled && (
                              <div className="text-xs">
                                {isDryRun ? (
                                  <span className="text-yellow-600"><span key={tUi("dryRun")}>{tUi("dryRun")}</span></span>
                                ) : isCreateDraft ? (
                                  <span className="text-purple-600"><span key={tUi("drafts")}>{tUi("drafts")}</span></span>
                                ) : (
                                  <span className="text-green-600"><span key={tUi("sending")}>{tUi("sending")}</span></span>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Category Settings (only when enabled) */}
                          {isEnabled && (
                            <div className="px-2.5 pb-2.5 pt-0 border-t bg-gray-50/50">
                              <RadioGroup
                                value={isDryRun ? "dry_run" : isCreateDraft ? "draft" : "send"}
                                onValueChange={(value) =>
                                  handleCategoryDeliveryModeChange(category, value as "dry_run" | "draft" | "send")
                                }
                                disabled={isSaving}
                                className="flex items-center gap-3 pt-2"
                              >
                                <div className="flex items-center gap-1.5">
                                  <RadioGroupItem
                                    value="dry_run"
                                    id={`dry-run-${category}`}
                                    className="h-3.5 w-3.5"
                                  />
                                  <Label
                                    htmlFor={`dry-run-${category}`}
                                    className="text-xs text-gray-600 cursor-pointer"
                                  >
                                    <span key={tUi("dryRun")}>{tUi("dryRun")}</span></Label>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <RadioGroupItem
                                    value="draft"
                                    id={`draft-${category}`}
                                    className="h-3.5 w-3.5"
                                  />
                                  <Label
                                    htmlFor={`draft-${category}`}
                                    className="text-xs text-gray-600 cursor-pointer"
                                  >
                                    <span key={tUi("drafts")}>{tUi("drafts")}</span></Label>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <RadioGroupItem
                                    value="send"
                                    id={`send-${category}`}
                                    className="h-3.5 w-3.5"
                                  />
                                  <Label
                                    htmlFor={`send-${category}`}
                                    className="text-xs text-gray-600 cursor-pointer"
                                  >
                                    <span key={tUi("send")}>{tUi("send")}</span></Label>
                                </div>
                              </RadioGroup>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 p-3 border rounded-lg bg-gray-50">
                    <span key={tUi("noCategoriesConfiguredPleaseSetUpClassificationCategories")}>{tUi("noCategoriesConfiguredPleaseSetUpClassificationCategories")}</span></div>
                )}
              </div>

              {/* Confidence & Start Date in one row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Minimum Confidence */}
                <div className="space-y-2">
                  <Label htmlFor="min-confidence" className="text-sm font-medium">
                    <span><span key={tUi("minConfidence")}>{tUi("minConfidence")}</span></span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-4 h-4 inline ml-1 text-gray-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p><span key={tUi("onlyReplyIfClassificationConfidenceIsAboveThis")}>{tUi("onlyReplyIfClassificationConfidenceIsAboveThis")}</span></p>
                      </TooltipContent>
                    </Tooltip>
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="min-confidence"
                      type="number"
                      min="0"
                      max="100"
                      value={autoReply.min_confidence || 70}
                      onChange={(e) => handleConfidenceChange(e.target.value)}
                      onBlur={(e) => handleConfidenceChange(e.target.value)}
                      className="text-sm w-20"
                      disabled={isSaving}
                    />
                    <span className="text-sm text-gray-500">%</span>
                  </div>
                </div>

                {/* Start Date */}
                <div className="space-y-2">
                  <Label htmlFor="start-date" className="text-sm font-medium">
                    <span><span key={tUi("startDate")}>{tUi("startDate")}</span></span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-4 h-4 inline ml-1 text-gray-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p><span key={tUi("onlyReplyToEmailsReceivedOnOrAfter")}>{tUi("onlyReplyToEmailsReceivedOnOrAfter")}</span></p>
                      </TooltipContent>
                    </Tooltip>
                  </Label>
                  <Input
                    id="start-date"
                    type="date"
                    value={autoReply.start_date || ""}
                    onChange={(e) => handleStartDateChange(e.target.value)}
                    className="text-sm"
                    disabled={isSaving}
                  />
                </div>
              </div>

              {/* Pause on Human Reply */}
              <div className="flex items-center justify-between p-4 border rounded-lg bg-orange-50 border-orange-200">
                <div className="flex items-center gap-2">
                  <UserCheck className="w-4 h-4 text-orange-600 shrink-0" />
                  <div>
                    <Label><span key={tUi("pauseOnHumanReply")}>{tUi("pauseOnHumanReply")}</span></Label>
                    <p className="text-xs text-gray-500">
                      <span key={tUi("completelySkipAIProcessingWhenATeamMember")}>{tUi("completelySkipAIProcessingWhenATeamMember")}</span></p>
                  </div>
                </div>
                <Switch
                  checked={autoReply.pause_on_human_reply ?? true}
                  onCheckedChange={(checked) => updateAutoReply({ pause_on_human_reply: checked })}
                  disabled={isSaving}
                />
              </div>

              {/* Skip when draft exists on thread */}
              <div className="flex items-center justify-between p-4 border rounded-lg bg-orange-50 border-orange-200">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-orange-600 shrink-0" />
                  <div>
                    <Label><span key={tUi("skipWhenDraftExistsOnThread")}>{tUi("skipWhenDraftExistsOnThread")}</span></Label>
                    <p className="text-xs text-gray-500">
                      <span key={tUi("whenEnabledTheAgentWillNotSendOr")}>{tUi("whenEnabledTheAgentWillNotSendOr")}</span></p>
                  </div>
                </div>
                <Switch
                  checked={autoReply.skip_when_draft_exists ?? true}
                  onCheckedChange={(checked) => updateAutoReply({ skip_when_draft_exists: checked })}
                  disabled={isSaving}
                />
              </div>

              {/* Mark as Read & Add Label */}
              <Collapsible open={markAsReadExpanded} onOpenChange={setMarkAsReadExpanded}>
                <div className="border rounded-lg bg-cyan-50 border-cyan-200 overflow-hidden">
                  <div className="flex items-center justify-between p-4">
                    <div
                      className="flex items-center gap-2 flex-1 cursor-pointer"
                      onClick={() => setMarkAsReadExpanded(!markAsReadExpanded)}
                    >
                      <MailCheck className="w-4 h-4 text-cyan-600" />
                      <Label className="text-sm font-medium text-gray-700 cursor-pointer">
                        <span key={tUi("postReplyActions")}>{tUi("postReplyActions")}</span></Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p><span key={tUi("actionsToPerformOnEmailsAfterSendingAn")}>{tUi("actionsToPerformOnEmailsAfterSendingAn")}</span></p>
                        </TooltipContent>
                      </Tooltip>
                      {(markAsReadEnabled || labels.incoming || labels.outgoing) && (
                        <Badge variant="secondary" className="text-xs bg-cyan-100 text-cyan-700 ml-2">
                          {[
                            markAsReadEnabled && "Mark Read",
                            (labels.incoming || labels.outgoing) && "Labels",
                          ].filter(Boolean).join(" + ")}
                        </Badge>
                      )}
                      {markAsReadExpanded ? (
                        <ChevronUp className="w-4 h-4 text-gray-500 ml-auto mr-2" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-500 ml-auto mr-2" />
                      )}
                    </div>
                  </div>

                  <CollapsibleContent>
                    <div className="space-y-4 px-4 pb-4">
                      {/* Gmail Modify Connection Status */}
                      <OAuthScopeConnectionStatus
                        isLoading={isLoadingGmailModify}
                        inboxes={gmailModifyInboxes.map((i) => ({
                          id: i.id,
                          email: i.email,
                          hasScope: i.hasGmailModifyScope,
                        }))}
                        onConnect={(inboxId) => connectGmailModify(inboxId)}
                        icon={Mail}
                        loadingText="Checking inbox permissions..."
                        connectButtonText="Grant Access"
                        connectedText="Ready"
                        allConnectedText="All inboxes have permission to modify emails"
                      />

                      {/* Gmail Modify Error */}
                      {gmailModifyError && (
                        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
                          <p className="text-sm text-red-700">{gmailModifyError}</p>
                        </div>
                      )}

                      {/* Mark as Read Toggle */}
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                        <div className="flex items-center gap-2">
                          <MailCheck className="w-4 h-4 text-cyan-600" />
                          <div>
                            <Label className="text-sm font-medium"><span key={tUi("markAsRead")}>{tUi("markAsRead")}</span></Label>
                            <p className="text-xs text-muted-foreground">
                              <span key={tUi("markEmailsAsReadAfterSendingAutoReply")}>{tUi("markEmailsAsReadAfterSendingAutoReply")}</span></p>
                          </div>
                        </div>
                        <Switch
                          checked={markAsReadEnabled}
                          onCheckedChange={(checked) => updateMarkAsReadEnabled(checked)}
                          disabled={isSaving || isSavingGmailModify || (!markAsReadEnabled && !gmailModifyInboxes.some(i => i.hasGmailModifyScope))}
                        />
                      </div>

                      {/* Gmail Labels */}
                      <Collapsible open={labelsExpanded} onOpenChange={setLabelsExpanded}>
                        <div className="bg-white rounded-lg border overflow-hidden">
                          <div className="flex items-center justify-between p-3">
                            <div
                              className="flex items-center gap-2 flex-1 cursor-pointer"
                              onClick={() => setLabelsExpanded(!labelsExpanded)}
                            >
                              <Tag className="w-4 h-4 text-cyan-600" />
                              <div>
                                <Label className="text-sm font-medium cursor-pointer"><span key={tUi("gmailLabels")}>{tUi("gmailLabels")}</span></Label>
                                <p className="text-xs text-muted-foreground">
                                  <span key={tUi("addLabelsToIncomingEmailsAndAIResponses")}>{tUi("addLabelsToIncomingEmailsAndAIResponses")}</span></p>
                              </div>
                              {(labels.incoming || labels.outgoing) && (
                                <div className="flex items-center gap-1 ml-2">
                                  {labels.incoming && (
                                    <span
                                      className="px-1.5 py-0.5 rounded text-[10px] font-medium"
                                      style={{ backgroundColor: labels.incoming.backgroundColor, color: labels.incoming.textColor }}
                                    >
                                      {labels.incoming.name}
                                    </span>
                                  )}
                                  {labels.outgoing && (
                                    <span
                                      className="px-1.5 py-0.5 rounded text-[10px] font-medium"
                                      style={{ backgroundColor: labels.outgoing.backgroundColor, color: labels.outgoing.textColor }}
                                    >
                                      {labels.outgoing.name}
                                    </span>
                                  )}
                                </div>
                              )}
                              {labelsExpanded ? (
                                <ChevronUp className="w-4 h-4 text-gray-500 ml-auto" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-gray-500 ml-auto" />
                              )}
                            </div>
                            <Switch
                              checked={!!(labels.incoming || labels.outgoing)}
                              onCheckedChange={(checked) => {
                                if (!checked) {
                                  void updateLabels({ incoming: null, outgoing: null });
                                } else {
                                  setLabelsExpanded(true);
                                }
                              }}
                              disabled={isSavingGmailModify || !gmailModifyInboxes.some(i => i.hasGmailModifyScope)}
                              className="ml-2"
                            />
                          </div>

                          <CollapsibleContent>
                            {gmailModifyInboxes.some(i => i.hasGmailModifyScope) && (
                              <div className="space-y-4 px-3 pb-3 border-t pt-3">
                                {/* Incoming Label (AI Read) */}
                                <div className="space-y-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                  <Label className="text-sm font-medium text-blue-800"><span key={tUi("incomingEmailLabel")}>{tUi("incomingEmailLabel")}</span></Label>
                                  <p className="text-xs text-blue-600"><span key={tUi("appliedToEmailsThatAIHasProcessed")}>{tUi("appliedToEmailsThatAIHasProcessed")}</span></p>
                                  <Input
                                    placeholder={tUi("eGAIRead")}
                                    value={incomingLabelName}
                                    onChange={(e) => setIncomingLabelName(e.target.value)}
                                    className="text-sm bg-white"
                                    disabled={isSavingGmailModify}
                                  />
                                  <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-1">
                                      <span className="text-xs text-muted-foreground"><span key={tUi("bg")}>{tUi("bg")}</span></span>
                                      {["#4a86e8", "#16a766", "#ffad47", "#fb4c2f", "#a479e2", "#999999"].map((color) => (
                                        <button
                                          key={`in-bg-${color}`}
                                          type="button"
                                          onClick={() => setIncomingLabelBgColor(color)}
                                          className={cn(
                                            "w-5 h-5 rounded border",
                                            incomingLabelBgColor === color && "ring-2 ring-offset-1 ring-blue-500"
                                          )}
                                          style={{ backgroundColor: color }}
                                        />
                                      ))}
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <span className="text-xs text-muted-foreground"><span key={tUi("text")}>{tUi("text")}</span></span>
                                      {["#ffffff", "#000000"].map((color) => (
                                        <button
                                          key={`in-text-${color}`}
                                          type="button"
                                          onClick={() => setIncomingLabelTextColor(color)}
                                          className={cn(
                                            "w-5 h-5 rounded border",
                                            incomingLabelTextColor === color && "ring-2 ring-offset-1 ring-blue-500"
                                          )}
                                          style={{ backgroundColor: color }}
                                        />
                                      ))}
                                    </div>
                                    {incomingLabelName && (
                                      <span
                                        className="px-2 py-0.5 rounded text-xs font-medium ml-auto"
                                        style={{ backgroundColor: incomingLabelBgColor, color: incomingLabelTextColor }}
                                      >
                                        {incomingLabelName}
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Outgoing Label (AI Response) */}
                                <div className="space-y-2 p-3 bg-green-50 rounded-lg border border-green-200">
                                  <Label className="text-sm font-medium text-green-800"><span key={tUi("outgoingResponseLabel")}>{tUi("outgoingResponseLabel")}</span></Label>
                                  <p className="text-xs text-green-600"><span key={tUi("appliedToAIGeneratedReplies")}>{tUi("appliedToAIGeneratedReplies")}</span></p>
                                  <Input
                                    placeholder={tUi("eGAIResponse")}
                                    value={outgoingLabelName}
                                    onChange={(e) => setOutgoingLabelName(e.target.value)}
                                    className="text-sm bg-white"
                                    disabled={isSavingGmailModify}
                                  />
                                  <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-1">
                                      <span className="text-xs text-muted-foreground"><span key={tUi("bg")}>{tUi("bg")}</span></span>
                                      {["#16a766", "#4a86e8", "#ffad47", "#fb4c2f", "#a479e2", "#999999"].map((color) => (
                                        <button
                                          key={`out-bg-${color}`}
                                          type="button"
                                          onClick={() => setOutgoingLabelBgColor(color)}
                                          className={cn(
                                            "w-5 h-5 rounded border",
                                            outgoingLabelBgColor === color && "ring-2 ring-offset-1 ring-green-500"
                                          )}
                                          style={{ backgroundColor: color }}
                                        />
                                      ))}
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <span className="text-xs text-muted-foreground"><span key={tUi("text")}>{tUi("text")}</span></span>
                                      {["#ffffff", "#000000"].map((color) => (
                                        <button
                                          key={`out-text-${color}`}
                                          type="button"
                                          onClick={() => setOutgoingLabelTextColor(color)}
                                          className={cn(
                                            "w-5 h-5 rounded border",
                                            outgoingLabelTextColor === color && "ring-2 ring-offset-1 ring-green-500"
                                          )}
                                          style={{ backgroundColor: color }}
                                        />
                                      ))}
                                    </div>
                                    {outgoingLabelName && (
                                      <span
                                        className="px-2 py-0.5 rounded text-xs font-medium ml-auto"
                                        style={{ backgroundColor: outgoingLabelBgColor, color: outgoingLabelTextColor }}
                                      >
                                        {outgoingLabelName}
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Save Button */}
                                <div className="flex items-center justify-between pt-2 border-t">
                                  <div className="flex items-center gap-2">
                                    {(incomingLabelName || outgoingLabelName) && (
                                      <>
                                        <span className="text-xs text-muted-foreground"><span key={tUi("gmailPreview")}>{tUi("gmailPreview")}</span></span>
                                        {incomingLabelName && (
                                          <span
                                            className="px-2 py-0.5 rounded text-xs font-medium"
                                            style={{ backgroundColor: incomingLabelBgColor, color: incomingLabelTextColor }}
                                          >
                                            {incomingLabelName}
                                          </span>
                                        )}
                                        {outgoingLabelName && (
                                          <span
                                            className="px-2 py-0.5 rounded text-xs font-medium"
                                            style={{ backgroundColor: outgoingLabelBgColor, color: outgoingLabelTextColor }}
                                          >
                                            {outgoingLabelName}
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </div>
                                  <Button
                                    type="button"
                                    size="sm"
                                    disabled={isSavingGmailModify || (() => {
                                      // Compare normalized values to detect actual changes
                                      const incomingTrimmed = incomingLabelName.trim();
                                      const outgoingTrimmed = outgoingLabelName.trim();

                                      const incomingUnchanged = incomingTrimmed
                                        ? (labels.incoming?.name === incomingTrimmed &&
                                           labels.incoming?.backgroundColor === incomingLabelBgColor &&
                                           labels.incoming?.textColor === incomingLabelTextColor)
                                        : !labels.incoming;

                                      const outgoingUnchanged = outgoingTrimmed
                                        ? (labels.outgoing?.name === outgoingTrimmed &&
                                           labels.outgoing?.backgroundColor === outgoingLabelBgColor &&
                                           labels.outgoing?.textColor === outgoingLabelTextColor)
                                        : !labels.outgoing;

                                      return incomingUnchanged && outgoingUnchanged;
                                    })()}
                                    onClick={() => void updateLabels({
                                      incoming: incomingLabelName.trim() ? {
                                        name: incomingLabelName.trim(),
                                        backgroundColor: incomingLabelBgColor,
                                        textColor: incomingLabelTextColor,
                                      } : null,
                                      outgoing: outgoingLabelName.trim() ? {
                                        name: outgoingLabelName.trim(),
                                        backgroundColor: outgoingLabelBgColor,
                                        textColor: outgoingLabelTextColor,
                                      } : null,
                                    })}
                                  >
                                    {isSavingGmailModify ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      tUi("saveLabels")
                                    )}
                                  </Button>
                                </div>
                              </div>
                            )}
                          </CollapsibleContent>
                        </div>
                      </Collapsible>

                      <p className="text-xs text-muted-foreground italic">
                        <span key={tUi("noteTheseActionsOnlyWorkWhenEmailsAre")}>{tUi("noteTheseActionsOnlyWorkWhenEmailsAre")}</span></p>
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            </CardContent>
          </Card>


        </>
      )}
      {section === "tools" && (
        <>
          {!autoReply.enabled && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              <span key={tUi("autoReplyIsOffYouCanConfigureTools")}>{tUi("autoReplyIsOffYouCanConfigureTools")}</span></p>
          )}
          {/* AI Behavior */}
          <Card>
            <CardHeader>
              <CardTitle><span key={tUi("aITools")}>{tUi("aITools")}</span></CardTitle>
              <CardDescription>
                <span key={tUi("configureAICapabilitiesForAutoReplies")}>{tUi("configureAICapabilitiesForAutoReplies")}</span></CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Use AI Tools */}
              <div className="flex items-center justify-between p-4 border rounded-lg bg-green-50 border-green-200">
                <div className="flex items-center gap-2">
                  <Bot className="w-4 h-4 text-green-600" />
                  <div>
                    <span className="text-sm font-medium text-gray-700">
                      <span key={tUi("enableAIPoweredTools")}>{tUi("enableAIPoweredTools")}</span></span>
                    <p className="text-xs text-gray-500">
                      <span key={tUi("allowAIToUseToolsForSmarterResponses")}>{tUi("allowAIToUseToolsForSmarterResponses")}</span></p>
                  </div>
                </div>
                <Switch
                  id="use-ai-tools"
                  checked={autoReply.use_ai_tools || false}
                  onCheckedChange={handleUseAiToolsChange}
                  disabled={isSaving}
                />
              </div>

              {autoReply.use_ai_tools && (
                <AgentToolCategory
                  title={tUi("generalTools")}
                  description={tUi("generalToolsDescription")}
                  tone="general"
                  icon={Wrench}
                >
                  {/* Smart Skip */}
                  {autoReply.use_ai_tools && (
                    <Collapsible open={skipReplyExpanded} onOpenChange={setSkipReplyExpanded}>
                      <div className="border rounded-lg bg-amber-50 border-amber-200 overflow-hidden">
                        <div className="flex items-center justify-between p-4">
                          <div
                            className="flex items-center gap-2 flex-1 cursor-pointer"
                            onClick={() => setSkipReplyExpanded(!skipReplyExpanded)}
                          >
                            <SkipForward className="w-4 h-4 text-amber-600" />
                            <Label className="text-sm font-medium text-gray-700 cursor-pointer">
                              <span key={tUi("smartSkip")}>{tUi("smartSkip")}</span></Label>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p><span key={tUi("aICanSkipReplyingWhenNoResponseIs")}>{tUi("aICanSkipReplyingWhenNoResponseIs")}</span></p>
                              </TooltipContent>
                            </Tooltip>
                            {skipReplyExpanded ? (
                              <ChevronUp className="w-4 h-4 text-gray-500 ml-auto mr-2" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-gray-500 ml-auto mr-2" />
                            )}
                          </div>
                          <Switch
                            checked={autoReply.allow_skip || false}
                            onCheckedChange={(checked) => updateAutoReply({ allow_skip: checked })}
                            disabled={isSaving}
                          />
                        </div>

                        <CollapsibleContent>
                          <div className="space-y-4 px-4 pb-4">
                            <p className="text-xs text-muted-foreground">
                              <span key={tUi("whenEnabledAICanDecideNotToReply")}>{tUi("whenEnabledAICanDecideNotToReply")}</span></p>

                            {/* Tool Instructions Button */}
                            <div>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setSkipReplyInstructionsOpen(true)}
                                className="text-xs"
                              >
                                <Settings className="w-3 h-3 mr-1.5" />
                                <span><span key={tUi("configureAIInstructions")}>{tUi("configureAIInstructions")}</span></span>
                              </Button>
                              <p className="text-xs text-muted-foreground mt-1">
                                <span key={tUi("customizeHowAIDecidesWhenToSkipReplying")}>{tUi("customizeHowAIDecidesWhenToSkipReplying")}</span></p>
                            </div>
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  )}

                  {/* Escalate to Human */}
                  {autoReply.use_ai_tools && (
                    <Collapsible open={handoverExpanded} onOpenChange={setHandoverExpanded}>
                      <div className="border rounded-lg bg-red-50 border-red-200 overflow-hidden">
                        <div className="flex items-center justify-between p-4">
                          <div
                            className="flex items-center gap-2 flex-1 cursor-pointer"
                            onClick={() => setHandoverExpanded(!handoverExpanded)}
                          >
                            <UserX className="w-4 h-4 text-red-600" />
                            <Label className="text-sm font-medium text-gray-700 cursor-pointer">
                              <span key={tUi("escalateToHuman")}>{tUi("escalateToHuman")}</span></Label>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p><span key={tUi("detectFrustratedCustomersAndHandOverToHuman")}>{tUi("detectFrustratedCustomersAndHandOverToHuman")}</span></p>
                              </TooltipContent>
                            </Tooltip>
                            {handoverExpanded ? (
                              <ChevronUp className="w-4 h-4 text-gray-500 ml-auto mr-2" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-gray-500 ml-auto mr-2" />
                            )}
                          </div>
                          <Switch
                            checked={humanHandover.enabled || false}
                            onCheckedChange={handleHandoverToggle}
                            disabled={isSaving}
                          />
                        </div>

                        <CollapsibleContent>
                          <div className="space-y-4 px-4 pb-4">
                            <p className="text-xs text-muted-foreground">
                              <span key={tUi("whenEnabledAIWillDetectFrustratedCustomersAnd")}>{tUi("whenEnabledAIWillDetectFrustratedCustomersAnd")}</span></p>

                            {/* CRM Pipeline/Stage Selection */}
                            {hasCrmForHandover ? (
                              <div className="p-3 bg-white rounded-lg border">
                                <HandoverPipelineSelector
                                  pipelines={crmPipelines}
                                  stages={handoverStages}
                                  selectedPipelineId={crmBinding?.config?.handover?.pipeline_id}
                                  selectedStageId={crmBinding?.config?.handover?.stage_id}
                                  isLoadingPipelines={isLoadingCrmPipelines}
                                  isSaving={isSavingCrm}
                                  showWarning={humanHandover.enabled}
                                  compact
                                  onPipelineChange={handleHandoverPipelineChange}
                                  onStageChange={handleHandoverStageChange}
                                />
                              </div>
                            ) : (
                              <div className="text-xs text-gray-500 p-2 bg-gray-100 rounded">
                                <span key={tUi("configureCRMInTheIntegrationsTabToSet")}>{tUi("configureCRMInTheIntegrationsTabToSet")}</span></div>
                            )}

                            {/* Notification channels */}
                            <div className="space-y-1.5">
                              <Label className="text-sm font-medium"><span key={tUi("notificationChannels")}>{tUi("notificationChannels")}</span></Label>
                              <p className="text-xs text-muted-foreground">
                                <span key={tUi("whereTheEscalationAlertIsDeliveredInAddition")}>{tUi("whereTheEscalationAlertIsDeliveredInAddition")}</span></p>
                              <HandoverChannelPicker
                                value={humanHandover.notification_channel_ids ?? []}
                                onChange={handleHandoverChannelsChange}
                                disabled={isSaving}
                              />
                            </div>

                            {/* Tool Instructions Button */}
                            <div>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setHandoverInstructionsOpen(true)}
                                className="text-xs"
                              >
                                <Settings className="w-3 h-3 mr-1.5" />
                                <span><span key={tUi("configureAIInstructions")}>{tUi("configureAIInstructions")}</span></span>
                              </Button>
                              <p className="text-xs text-muted-foreground mt-1">
                                <span key={tUi("customizeHowAIDecidesWhenToHandOver")}>{tUi("customizeHowAIDecidesWhenToHandOver")}</span></p>
                            </div>
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  )}

                  {/* Email Forward */}
                  {autoReply.use_ai_tools && (
                    <Collapsible open={emailForwardExpanded} onOpenChange={setEmailForwardExpanded}>
                      <div className="border rounded-lg bg-blue-50 border-blue-200 overflow-hidden">
                        <div className="flex items-center justify-between p-4">
                          <div
                            className="flex items-center gap-2 flex-1 cursor-pointer"
                            onClick={() => setEmailForwardExpanded(!emailForwardExpanded)}
                          >
                            <Forward className="w-4 h-4 text-blue-600" />
                            <Label className="text-sm font-medium text-gray-700 cursor-pointer">
                              <span key={tUi("emailForward")}>{tUi("emailForward")}</span></Label>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p><span key={tUi("allowAIToForwardEmailsToOtherRecipients")}>{tUi("allowAIToForwardEmailsToOtherRecipients")}</span></p>
                              </TooltipContent>
                            </Tooltip>
                            {emailForwardExpanded ? (
                              <ChevronUp className="w-4 h-4 text-gray-500 ml-auto mr-2" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-gray-500 ml-auto mr-2" />
                            )}
                          </div>
                          <Switch
                            checked={emailForward.enabled || false}
                            onCheckedChange={handleEmailForwardToggle}
                            disabled={isSaving}
                          />
                        </div>

                        <CollapsibleContent>
                          <div className="space-y-4 px-4 pb-4">
                            {/* Tool Instructions Button */}
                            <p className="text-xs text-muted-foreground">
                              <span key={tUi("whenEnabledAICanForwardCustomerEmailsTo")}>{tUi("whenEnabledAICanForwardCustomerEmailsTo")}</span></p>
                            <div>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setEmailForwardInstructionsOpen(true)}
                                className="text-xs"
                              >
                                <Settings className="w-3 h-3 mr-1.5" />
                                <span><span key={tUi("configureAIInstructions")}>{tUi("configureAIInstructions")}</span></span>
                              </Button>
                              <p className="text-xs text-muted-foreground mt-1">
                                <span key={tUi("customizeHowAIDecidesWhenAndWhereTo")}>{tUi("customizeHowAIDecidesWhenAndWhereTo")}</span></p>
                            </div>
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  )}

                  {/* Calendar Booking */}
                  {autoReply.use_ai_tools && (
                    <Collapsible open={calendarBookingExpanded} onOpenChange={setCalendarBookingExpanded}>
                      <div className="border rounded-lg bg-indigo-50 border-indigo-200 overflow-hidden">
                        <div className="flex items-center justify-between p-4">
                          <div
                            className="flex items-center gap-2 flex-1 cursor-pointer"
                            onClick={() => setCalendarBookingExpanded(!calendarBookingExpanded)}
                          >
                            <Calendar className="w-4 h-4 text-indigo-600" />
                            <Label className="text-sm font-medium text-gray-700 cursor-pointer">
                              <span key={tUi("calendarBooking")}>{tUi("calendarBooking")}</span></Label>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p><span key={tUi("aICanCheckAvailabilityAndBookMeetingsIn")}>{tUi("aICanCheckAvailabilityAndBookMeetingsIn")}</span></p>
                              </TooltipContent>
                            </Tooltip>
                            {calendarBookingExpanded ? (
                              <ChevronUp className="w-4 h-4 text-gray-500 ml-auto mr-2" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-gray-500 ml-auto mr-2" />
                            )}
                          </div>
                          <Switch
                            checked={calendarConfig?.enabled || false}
                            onCheckedChange={(checked) => updateCalendarConfig({ enabled: checked })}
                            disabled={isSaving || isSavingCalendar || !calendarInboxes.some(i => i.hasCalendarScope)}
                          />
                        </div>

                        <CollapsibleContent>
                          <div className="space-y-4 px-4 pb-4">
                            <p className="text-xs text-muted-foreground">
                              <span key={tUi("whenEnabledAICanCheckYourCalendarAvailability")}>{tUi("whenEnabledAICanCheckYourCalendarAvailability")}</span></p>

                            {/* Calendar Connection Status */}
                            <OAuthScopeConnectionStatus
                              isLoading={isLoadingCalendar}
                              inboxes={calendarInboxes.map((i) => ({
                                id: i.id,
                                email: i.email,
                                hasScope: i.hasCalendarScope,
                              }))}
                              onConnect={(inboxId) => connectCalendar(inboxId)}
                              icon={Calendar}
                              loadingText="Checking calendar status..."
                              connectButtonText="Grant Access"
                              connectedText="Connected"
                              allConnectedText="All inboxes have calendar access"
                            />

                            {/* Calendar Error */}
                            {calendarError && (
                              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
                                <p className="text-sm text-red-700">{calendarError}</p>
                              </div>
                            )}

                            {/* Settings (only show when at least one inbox connected and enabled) */}
                            {calendarInboxes.some(i => i.hasCalendarScope) && calendarConfig?.enabled && (
                              <Collapsible>
                                <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-white rounded-lg border hover:bg-gray-50 transition-colors">
                                  <span className="text-sm font-medium"><span key={tUi("workingHoursMeetingSettings")}>{tUi("workingHoursMeetingSettings")}</span></span>
                                  <ChevronDown className="w-4 h-4 text-gray-500" />
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                  <div className="mt-2 space-y-4 p-3 bg-white rounded-lg border">
                                    <div className="space-y-4">
                                      <h4 className="text-sm font-medium text-gray-700"><span key={tUi("workingHours")}>{tUi("workingHours")}</span></h4>
                                      <WorkingHoursConfigEditor
                                        value={calendarConfig.working_hours}
                                        onChange={(working_hours) => updateCalendarConfig({ working_hours })}
                                        disabled={isSavingCalendar}
                                      />
                                    </div>

                                    <div className="border-t pt-4 space-y-3">
                                      <h4 className="text-sm font-medium text-gray-700"><span key={tUi("meetingDefaults")}>{tUi("meetingDefaults")}</span></h4>
                                      <MeetingDefaultsConfig
                                        value={calendarConfig.meeting_defaults}
                                        onChange={(meeting_defaults) => updateCalendarConfig({ meeting_defaults })}
                                        disabled={isSavingCalendar}
                                      />
                                    </div>
                                  </div>
                                </CollapsibleContent>
                              </Collapsible>
                            )}

                            {/* Tool Instructions Buttons */}
                            <div className="space-y-2">
                              <p className="text-xs text-muted-foreground">
                                <span key={tUi("customizeHowAIHandlesCalendarRequests")}>{tUi("customizeHowAIHandlesCalendarRequests")}</span></p>
                              <div className="flex flex-wrap gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setCalendarAvailabilityInstructionsOpen(true)}
                                  className="text-xs"
                                >
                                  <Settings className="w-3 h-3 mr-1.5" />
                                  <span><span key={tUi("checkAvailability")}>{tUi("checkAvailability")}</span></span>
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setCalendarBookMeetingInstructionsOpen(true)}
                                  className="text-xs"
                                >
                                  <Settings className="w-3 h-3 mr-1.5" />
                                  <span><span key={tUi("bookMeeting")}>{tUi("bookMeeting")}</span></span>
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  )}
                </AgentToolCategory>
              )}

              {autoReply.use_ai_tools && (
                <AgentToolCategory
                  title={tUi("scheduleAction")}
                  description={tUi.rich("scheduleActionDescription", { b: chunks => <strong>{chunks}</strong> })}
                  tone="general"
                  icon={CalendarClock}
                >
                  {/* Schedule callback */}
                  {autoReply.use_ai_tools && (
                    <ScheduleCallbackToolSettings
                      scopeType="email_agent"
                      config={scheduleCallback}
                      enabled={scheduleCallback.enabled === true}
                      disabled={isSaving}
                      onConfigChange={(schedule_call) => {
                        void saveConfig({ schedule_call });
                      }}
                      onEnabledChange={(enabled, callbackConfig) => {
                        void saveConfig({
                          schedule_call: { ...callbackConfig, enabled },
                        });
                      }}
                      onOpenInstructions={() => setScheduleCallbackInstructionsOpen(true)}
                    />
                  )}

                  {autoReply.use_ai_tools && (
                    <ScheduleEmailToolSettings settings={config.send_email} allowEnable sourceAgentId={agent.id} disabled={isSaving}
                      onSave={async settings => { await saveConfig({ send_email: settings }); }} />
                  )}
                </AgentToolCategory>
              )}

              {/* ───────────── Shopify Tools section — needs ≥1 active integration ───────────── */}
              {/* Section visibility is gated by `hasStorefrontToolsAccess` (≥1 active */}
              {/* shopify_integrations row, channel-agnostic). Order Lookup additionally */}
              {/* requires `uses_api=true` on the integration — locked tile + tooltip. */}
              {/* Catalog + Policies query the live Storefront MCP via the integration domain. */}
              {/* Collapsed by default; header shows how many tools are enabled. */}
              {autoReply.use_ai_tools && hasStorefrontToolsAccess && (() => {
                // Order Lookup is locked (shown OFF) without an API-enabled
                // integration — don't count it as enabled in that case.
                const enabledShopifyToolsCount =
                  (autoReply.shopify_order_enabled && hasActiveShopify ? 1 : 0) +
                  (autoReply.shopify_catalog_enabled ? 1 : 0) +
                  (autoReply.shopify_policies_enabled ? 1 : 0);

                // Per-tool dynamic wiring keyed on the shared tile metadata.
                const shopifyToolChecked: Record<ShopifyToolName, boolean> = {
                  get_shopify_order: autoReply.shopify_order_enabled ?? false,
                  search_shopify_catalog: autoReply.shopify_catalog_enabled ?? false,
                  search_shopify_policies: autoReply.shopify_policies_enabled ?? false,
                };
                const shopifyToolToggle: Record<ShopifyToolName, (v: boolean) => void> = {
                  get_shopify_order: (v) => updateAutoReply({ shopify_order_enabled: v }),
                  search_shopify_catalog: (v) => updateAutoReply({ shopify_catalog_enabled: v }),
                  search_shopify_policies: (v) => updateAutoReply({ shopify_policies_enabled: v }),
                };
                const shopifyToolConfigure: Record<ShopifyToolName, () => void> = {
                  get_shopify_order: () => setShopifyOrderInstructionsOpen(true),
                  search_shopify_catalog: () => setShopifyCatalogInstructionsOpen(true),
                  search_shopify_policies: () => setShopifyPoliciesInstructionsOpen(true),
                };

                return (
                  <Collapsible
                    open={shopifyToolsExpanded}
                    onOpenChange={setShopifyToolsExpanded}
                  >
                    <div className="border-l-2 border-emerald-300 pl-3">
                      <CollapsibleTrigger asChild>
                        <button
                          type="button"
                          className="flex items-center gap-2 w-full text-left py-1"
                        >
                          <ShoppingBag className="w-4 h-4 text-emerald-700" />
                          <Label className="text-sm font-semibold text-emerald-900 cursor-pointer">
                            <span key={tUi("shopifyTools")}>{tUi("shopifyTools")}</span></Label>
                          {enabledShopifyToolsCount > 0 && (
                            <Badge
                              variant="secondary"
                              className="text-[10px] bg-emerald-100 text-emerald-800 border-emerald-300"
                            >
                              {enabledShopifyToolsCount} <span><span key={tUi("enabled")}>{tUi("enabled")}</span></span>
                            </Badge>
                          )}
                          {shopifyToolsExpanded ? (
                            <ChevronUp className="w-4 h-4 text-gray-500 ml-auto" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-500 ml-auto" />
                          )}
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="space-y-2 pt-2">
                          {/* Shop domain selector — applies to Catalog and Policies tools */}
                          <ShopDomainField
                            value={autoReply.shopify_shop_domain}
                            onChange={(shopify_shop_domain) =>
                              updateAutoReply({ shopify_shop_domain })
                            }
                            integrations={shopifyIntegrations}
                            disabled={isSaving}
                            isSaving={isSaving}
                          />

                          {/* Three tools in a row: Order Lookup / Catalog / Policies.
                              Static copy/icon/tone/badge come from the shared
                              SHOPIFY_TOOL_TILE_DEFS; only the wiring is per-channel. */}
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            {SHOPIFY_TOOL_TILE_DEFS.map((def) => (
                              <ShopifyToolTile
                                key={def.name}
                                icon={def.icon}
                                title={def.title}
                                tooltip={def.tooltip}
                                tone={def.tone}
                                badge={def.badge}
                                checked={shopifyToolChecked[def.name]}
                                onCheckedChange={shopifyToolToggle[def.name]}
                                onConfigure={shopifyToolConfigure[def.name]}
                                disabled={isSaving}
                                lockedReason={
                                  def.requiresApi && !hasActiveShopify
                                    ? ORDER_LOOKUP_LOCKED_REASON
                                    : undefined
                                }
                              />
                            ))}
                          </div>

                          {/* Explain the "Live" badge and warn about KB overlap */}
                          <ShopifyLiveDataNote />
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                );
              })()}

              {/* CRM opportunity tools — sibling grouped category next to Shopify. */}
              {/* Rendered with the master AI-tools toggle; `hasBinding` gates the */}
              {/* refresh-schema probe so an agent with no/disabled CRM binding doesn't */}
              {/* fire a guaranteed 404. */}
              {autoReply.use_ai_tools && (
                <CrmToolsGroup
                  channel="email"
                  agentId={agent.id}
                  canManage={true}
                  hasBinding={crmBinding ? crmBinding.config?.enabled !== false : hasCrmTenantDefault}
                  onOpenUpdateFieldsInstructions={() => setCrmUpdateFieldsInstructionsOpen(true)}
                  onOpenMoveStageInstructions={() => setCrmMoveStageInstructionsOpen(true)}
                />
              )}

              {autoReply.use_ai_tools && (
                <AgentDeliveryToolsGroup
                  agentId={agent.id}
                  scopeType="email_agent"
                  canManage
                />
              )}
            </CardContent>
          </Card>

          {/* AI Model override (per-agent, permission-gated) — below AI Behavior */}
          <AgentModelOverride scopeType="email_agent" scopeId={agent.id} />

        </>
      )}

      {/* Generate Prompt Modal */}
      <GeneratePromptModal
        open={generatePromptModalOpen}
        onOpenChange={setGeneratePromptModalOpen}
        onApplyPrompt={handleApplyGeneratedPrompt}
      />

      {/* Escalate-to-Human Instructions Dialog */}
      <ToolInstructionsDialog
        open={handoverInstructionsOpen}
        onOpenChange={setHandoverInstructionsOpen}
        toolName="human_handover"
        toolDisplayName="Escalate to Human"
        scopeType="email_agent"
        scopeId={agent.id}
      />

      {/* Email Forward Instructions Dialog */}
      <ToolInstructionsDialog
        open={emailForwardInstructionsOpen}
        onOpenChange={setEmailForwardInstructionsOpen}
        toolName="email_forward"
        toolDisplayName="Email Forward"
        scopeType="email_agent"
        scopeId={agent.id}
      />

      <ToolInstructionsDialog
        open={scheduleCallbackInstructionsOpen}
        onOpenChange={setScheduleCallbackInstructionsOpen}
        toolName="schedule_call"
        toolDisplayName="Schedule Call"
        scopeType="email_agent"
        scopeId={agent.id}
      />

      {/* Skip Reply Instructions Dialog */}
      <ToolInstructionsDialog
        open={skipReplyInstructionsOpen}
        onOpenChange={setSkipReplyInstructionsOpen}
        toolName="skip_reply"
        toolDisplayName="Smart Skip"
        scopeType="email_agent"
        scopeId={agent.id}
      />

      {/* Catalog/Policies dialog mounts: gated by Storefront access (Shopify */}
      {/* tenants only). Order Lookup dialog: only when there's an active */}
      {/* integration — otherwise the tile is locked and Configure is disabled. */}
      {/* Mirrors the same conditional structure as the messaging settings */}
      {/* file (`reply-settings.tsx`) for cross-file consistency. */}
      {hasStorefrontToolsAccess && (
        <>
          <ToolInstructionsDialog
            open={shopifyCatalogInstructionsOpen}
            onOpenChange={setShopifyCatalogInstructionsOpen}
            toolName="search_shopify_catalog"
            toolDisplayName="Shopify Catalog Search"
            scopeType="email_agent"
            scopeId={agent.id}
          />
          <ToolInstructionsDialog
            open={shopifyPoliciesInstructionsOpen}
            onOpenChange={setShopifyPoliciesInstructionsOpen}
            toolName="search_shopify_policies"
            toolDisplayName="Shopify Policies & FAQs"
            scopeType="email_agent"
            scopeId={agent.id}
          />
        </>
      )}
      {hasActiveShopify && (
        <ToolInstructionsDialog
          open={shopifyOrderInstructionsOpen}
          onOpenChange={setShopifyOrderInstructionsOpen}
          toolName="get_shopify_order"
          toolDisplayName="Shopify Order Lookup"
          scopeType="email_agent"
          scopeId={agent.id}
        />
      )}

      {/* Calendar Availability Instructions Dialog */}
      <ToolInstructionsDialog
        open={calendarAvailabilityInstructionsOpen}
        onOpenChange={setCalendarAvailabilityInstructionsOpen}
        toolName="check_calendar_availability"
        toolDisplayName="Check Calendar Availability"
        scopeType="email_agent"
        scopeId={agent.id}
      />

      {/* Calendar Book Meeting Instructions Dialog */}
      <ToolInstructionsDialog
        open={calendarBookMeetingInstructionsOpen}
        onOpenChange={setCalendarBookMeetingInstructionsOpen}
        toolName="book_calendar_meeting"
        toolDisplayName="Book Calendar Meeting"
        scopeType="email_agent"
        scopeId={agent.id}
      />

      {/* CRM: Update Opportunity Fields Instructions Dialog */}
      <ToolInstructionsDialog
        open={crmUpdateFieldsInstructionsOpen}
        onOpenChange={setCrmUpdateFieldsInstructionsOpen}
        toolName="crm_update_opportunity_fields"
        toolDisplayName="CRM: Update Opportunity Fields"
        scopeType="email_agent"
        scopeId={agent.id}
      />

      {/* CRM: Move Opportunity Stage Instructions Dialog */}
      <ToolInstructionsDialog
        open={crmMoveStageInstructionsOpen}
        onOpenChange={setCrmMoveStageInstructionsOpen}
        toolName="crm_move_opportunity_stage"
        toolDisplayName="CRM: Move Opportunity Stage"
        scopeType="email_agent"
        scopeId={agent.id}
      />

      {/* Enable Confirmation Dialog */}
      <AlertDialog open={enableDialogOpen} onOpenChange={setEnableDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              <span><span key={tUi("enableAutoReply")}>{tUi("enableAutoReply")}</span></span>
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>
                  <span><span key={tUi("pleaseSetThe")}>{tUi("pleaseSetThe")}</span></span> <strong><span key={tUi("startDate")}>{tUi("startDate")}</span></strong> <span><span key={tUi("toControlWhichEmailsWillReceiveAutoReplies")}>{tUi("toControlWhichEmailsWillReceiveAutoReplies")}</span></span>
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-amber-800 text-sm">
                    <span key={tUi("thisPreventsAccidentallyReplyingToOldEmailsThat")}>{tUi("thisPreventsAccidentallyReplyingToOldEmailsThat")}</span></p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="py-4">
            <Label htmlFor="enable-start-date" className="text-sm font-medium">
              <span key={tUi("startDate")}>{tUi("startDate")}</span></Label>
            <Input
              id="enable-start-date"
              type="date"
              value={pendingStartDate}
              onChange={(e) => setPendingStartDate(e.target.value)}
              className="mt-2"
            />
            <p className="text-xs text-muted-foreground mt-1">
              <span><span key={tUi("recommendedTodaySDate")}>{tUi("recommendedTodaySDate")}</span></span>{getTodayDateString()}<span>)</span>
            </p>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel><span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmEnable}
              disabled={!pendingStartDate || isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  <span key={tUi("enabling")}>{tUi("enabling")}</span></>
              ) : (
                tUi("enableAutoReply")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
